/**
 * DOCUMENTATION:
 * Component: AIDashboard
 * Purpose: Entry point for the AI & LLM Integration track.
 * * * Note for Junior Developers:
 * - This track bridges the gap between 'Code' and 'Cognition'.
 * - We focus on RAG (Retrieval-Augmented Generation) as the primary enterprise AI pattern.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Grid, Card, Typography, Button, Stack, styled, useTheme, useMediaQuery, Avatar
} from "@mui/material";

/* Icons */
import PsychologyIcon from '@mui/icons-material/Psychology';
import MemoryIcon from '@mui/icons-material/Memory';
import HubIcon from '@mui/icons-material/Hub';
import StorageIcon from '@mui/icons-material/Storage';

const bgImage = "https://images.unsplash.com/photo-1677442136019-21780ecad995"; // AI Neural Network Image

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.94), rgba(10, 5, 25, 0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
    color: "white",
}));

const ModuleCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(30, 20, 50, 0.8)",
    borderRadius: "16px",
    padding: theme.spacing(3),
    border: "1px solid rgba(167, 139, 250, 0.2)", // Purple/Violet theme for AI
    transition: "0.3s",
    "&:hover": {
        transform: "translateY(-5px)",
        borderColor: "#a78bfa",
    },
}));

export default function AIDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const modules = [
        { title: "Intro to RAG & Gemini", icon: <PsychologyIcon />, link: "/ai-intro-rag", color: "#a78bfa" },
        { title: "LangChain Orchestration", icon: <HubIcon />, link: "/ai-langchain-basics", color: "#4285F4" },
        { title: "Vector DBs & Embeddings", icon: <StorageIcon />, link: "/ai-vector-databases", color: "#34A853" },
        { title: "Final RAG Project", icon: <MemoryIcon />, link: "/ai-project-rag", color: "#FBBC04" },
    ];

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 8, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 900, color: "#a78bfa" }}>
                    AI & LLM ARCHITECTURE
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.7 }}>Building Intelligent, Context-Aware Applications</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                {modules.map((mod, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <ModuleCard>
                            <Stack spacing={2} alignItems="center" textAlign="center">
                                <Avatar sx={{ bgcolor: mod.color, width: 56, height: 56 }}>
                                    {mod.icon}
                                </Avatar>
                                <Typography variant="h6" fontWeight={800}>{mod.title}</Typography>
                                <Link to={mod.link} style={{ textDecoration: "none", width: '100%' }}>
                                    <Button fullWidth variant="outlined" sx={{ color: mod.color, borderColor: mod.color }}>
                                        Initialize
                                    </Button>
                                </Link>
                            </Stack>
                        </ModuleCard>
                    </Grid>
                ))}
            </Grid>
        </PageWrapper>
    );
}
